import {catchError, filter, map, mergeMap, take, tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {
    AuditId,
    IntegrationId,
    ProjectId,
    ReportSpecId,
    SpecGroupId,
    SpecId,
    SpecRef,
    TestExecutionId,
    FindingId,
    TestRunId,
    TestSessionId,
    TestTemplateId,
    TestObjectCollectionId,
    TestObjectId,
    TestObjectResolutionJobId,
    StateCollectorJobId,
    QASessionAssociationId,
    QASessionId,
    UserId
} from '../types/ids';
import {combineLatest, forkJoin, Observable, of, ReplaySubject, retry, Subject} from 'rxjs';
import {FindingCellAction, SpecCollectionDTO, TestApi} from '../api/rest/test.restapi';
import {EventsService, WebmateWebsocketEvent} from '@webmate-web-core/shared/services/events/events.service';
import {
    AdHocReportSpec,
    AssignmentSpec,
    CreateSpecDTO,
    CreateSpecGroupDTO,
    FilterSpec,
    GroupingSpec,
    ObservationTarget,
    Spec,
    SpecGroup,
    SpecGroupUpdateAction,
    SpecUpdateAction,
    TestExecutionSummary,
    TestReportSpec,
    Finding,
    FindingCreationData,
    FindingUpdateData,
    TestRun,
    TestRunCreationSpec,
    TestRunEvaluationStatus,
    TestRunReportConfig,
    TestSession,
    TestSessionInfo,
    TestTemplate,
    TestType,
    ContentCell, ContentCellId,
    FindingsQuerySpec, FindingsQueryResult, FindingInfo, FindingsStats, TestMgmtEventTypes, ObservationData,
    CreateTestObjectCollectionDTO,
    CreateTestObjectDTO,
    TestObject,
    TestObjectCollection,
    TestObjectCollectionSummary,
    TestObjectCollectionUpdateAction,
    TestObjectStateSummary,
    TestObjectResolutionJob,
    TestObjectsSourceSpec,
    CreateQASessionAssociationData,
    CreateQASessionData,
    OpenTestObjectInExpeditionRequest,
    QASession,
    QASessionAssociation,
    QASessionAssociationTargetType,
    UpdateQASessionData,
} from '@webmate-web-core/shared/types/testmgmt';
import {APIResult, ApiSourcedPaginationRequest, GenericApiResponse, QueryMetadata} from '@webmate-web-core/shared/api/apibase.api';
import {TagRule} from '@webmate-web-core/shared/types/tests/tag-rule';
import {Tag} from '@webmate-web-core/shared/types/tag';
import {isUUID} from '@webmate-web-core/shared/utils/utils';
import {WMDatatype, WMValue} from '@webmate-web-core/shared/types/base-types';
import {withResultCache} from '@webmate-web-core/shared/utils/cache-util';
import {IssueSeverity} from '../console/types/shared';
import {
    IssueReporterDefaults,
    IssueReporterInsertDTO,
    IssueReporterIntegration,
    IssueReporterProjectWithIssueTypes,
    IssueReportingInfo,
    IssueSourceType,
    IssueReporterParametersResponse
} from '@webmate-web-core/shared/types/integrations';
import {ReportId} from '../../../report/report_types';
import {BackendSpecForestNode} from '@webmate-web-core/shared/services/specs';
import {
    buildContainingTestObjectCollections,
    ContainingTestObjectCollection
} from '@webmate-web-core/shared/components/testmgmt/splitview/testobject-collections.utils';
import {buildCollectionMembershipCountMap} from '@webmate-web-core/shared/components/testmgmt/splitview/testobject-overview.utils';
import {AutomationPolicyConfig} from '@webmate-web-core/shared/types/browsersession';
import {TestRunReportData} from './console-test-run.service';
import {TestRunResultProjection} from '@webmate-web-core/shared/types/test-run-result-projection';

export interface CreateTestExecutionResult {
    testExecutionId: TestExecutionId;
    // testRunId is undefined if the Execution has not been started yet
    testRunId: TestRunId | undefined;
}

export interface IssueReporterParameter {
    name: string;
    required: boolean;
    optDisplayName?: string;
    optDescription?: string;
    parameterType: WMDatatype;
    size?: number;
    possibleValues?: string[][];
    default?: WMValue<any>;
    alternatives?: {label: string,value: any}[]; // used internally
    parameterKind?: string; // used internally
    customInput?: string; // used internally
}

export type TestObjectUpdateAction = {
    type: 'SetName';
    name: string;
} | {
    type: 'SetDescription';
    description?: string;
} | {
    type: 'SetGenerationSpec';
    generationSpec: any;
} | {
    type: 'SetMatchingSpec';
    matchingSpec: any;
} | {
    type: 'SetLabels';
    labels: Tag[];
} | {
    type: 'SetAutomationPolicy';
    automationPolicy?: AutomationPolicyConfig | null;
}


/**
 * Provides access to all available TestRuns and their Summaries.
 */
@Injectable({
    providedIn: 'root'
})
export class TestService {

    //  _____ _   _ _______ ______ _____  _   _          _
    // |_   _| \ | |__   __|  ____|  __ \| \ | |   /\   | |
    //   | | |  \| |  | |  | |__  | |__) |  \| |  /  \  | |
    //   | | | . ` |  | |  |  __| |  _  /| . ` | / /\ \ | |
    //  _| |_| |\  |  | |  | |____| | \ \| |\  |/ ____ \| |____
    // |_____|_| \_|  |_|  |______|_|  \_\_| \_/_/    \_\______|
    //

    constructor(private api: TestApi, private eventService: EventsService) {
    }

    getActiveAdhocTestRunsForUser$(projectId: ProjectId, userId: UserId): Observable<Array<TestRunReportData>> {
        const query = {
            aggregationSpec: [],
            groupingSpec: [],
            filterSpec: {
                deviceFilterSpecs: [],
                tagFilterSpecs: [],
                statusFilterSpec: ['pending_passed', 'pending_failed'],
                testTypeFilterSpec: ['adhoc'],
                userFilterSpec: [userId],
                timeSpec: {
                    type: 'RelativeTimeFilterSpec',
                    relativeTime: 'pastyear'
                }
            }
        };

        return this.queryAdhocTestReports$(projectId, query).pipe(
            mergeMap(res => {
                const results = (res?.data?.items ?? []) as Array<TestRunReportData>;
                if (results.length === 0) {
                    return of([] as Array<TestRunReportData>);
                }
                const executions$ = results.map(r =>
                    this.getTestExecution(r.executionId).pipe(map(e => ({execution: e, run: r})))
                );
                return combineLatest(executions$).pipe(
                    map(res => res.filter(e => e.execution.displayType === 'manualtest').map(e => e.run))
                );
            })
        );
    }

    private testTemplatesCache: {[p: ProjectId]: ReplaySubject<TestTemplate[]>} = {};

    private testRunCache: {[s: TestRunId ]: ReplaySubject<TestRun>} = {};
    private testRunsByExecutionCache: {[s: string /*TestExecutionId*/]: ReplaySubject<Array<TestRunId>>} = {};

    private findingCache: {[s: FindingId ]: ReplaySubject<Finding>} = {};
    private findingInfoCache: {[s: FindingId ]: ReplaySubject<FindingInfo>} = {};

    private testExecutionRequestsByTemplate: {[s: string /*TestId */]: ApiSourcedPaginationRequest<TestExecutionSummary>} = {};

    private _testEventsHandleByProject: { [s: string /* ProjectId*/]: Observable<WebmateWebsocketEvent> } = {}; //events

    private eventForwardingSubjects: { [s: string /* ProjectId*/]: Subject<WebmateWebsocketEvent> } = {}; //events

    private auditEventForwardingSubjects: { [s: string /* AuditId */]: Subject<any> } = {}; //events

    private findingUpdatedEventForwardingSubjects: { [s: string /* ProjectId */]: Subject<any> } = {}; //events

    private findingIndexUpdatedEventForwardingSubjects: { [s: string /* ProjectId */]: Subject<any> } = {}; //events
    private issueReporterCache: ReplaySubject<Array<IssueReporterIntegration>>;
    private testObjectCollectionDetailsByProjectCache: {[s: string /* ProjectId */]: ReplaySubject<TestObjectCollection[]>} = {};
    private testObjectCollectionProjectById: {[s: string /* TestObjectCollectionId */]: ProjectId} = {};

    private _registerEventsForProject(projectId: ProjectId): void {
        if (!this.eventForwardingSubjects[projectId]) {
            this.eventForwardingSubjects[projectId] = new Subject<any>();
        }
        if (isUUID(projectId) && this._testEventsHandleByProject[projectId] === undefined) {
            this._testEventsHandleByProject[projectId] = this.eventService.getEventsForProject(projectId); //register events!
            this._testEventsHandleByProject[projectId].pipe(filter(ev => ev.eventType.startsWith("TestMgmt.") || ev.eventType.startsWith("TestMail."))).subscribe(
                next => this._handleEvents(next, projectId)
            );
        }
    }

    private _handleEvents(e: WebmateWebsocketEvent, forProject: ProjectId): void {
        if (e === undefined) return;
        if (this.eventForwardingSubjects[forProject]) {
            this.eventForwardingSubjects[forProject].next(e);
        }
        if (e.eventType.startsWith("TestMgmt.TestRun.")) {
            if (this.testRunCache[e['testRunId']]){
                this.reloadTestRunById(e['testRunId']);
            } else if (e['executionId']) {
                this.reloadTestRunsForExecution(e['executionId']);
            }
        } else if (e.eventType.startsWith('TestMail.Mail.Received') && e.optTestRunId) {
            this.reloadTestRunById(e.optTestRunId);

        } else if (e.eventType.startsWith("TestMgmt.Finding.")) {
            const affectedFindings = (e['findings'] || (e['id'] ? [e['id']] : [])).map((tr: any) => tr.id);
            affectedFindings.forEach ((findingId: FindingId) => {
                if (!!this.findingCache[findingId]) {
                    // if we already have a cache entry for this result, we update it as someone might be interested in the new value
                    this.reloadFindingCacheEntry(findingId);
                    // this.findingCache[e['findingId']].next(e);
                }
                // otherwise we ignore the update
            });

            const affectedAudit = e['audit'];
            if (affectedAudit && this.auditEventForwardingSubjects[affectedAudit]) {
                this.auditEventForwardingSubjects[affectedAudit].next(e);
            }

            const affectedTestRun = e['testRun'];
            if (affectedTestRun) {
                this.reloadTestRunById(affectedTestRun)
            }

            if (e.eventType === TestMgmtEventTypes.FindingUpdated || e.eventType === TestMgmtEventTypes.FindingAdded || TestMgmtEventTypes.FindingDeleted) {
                const affectedProject = e.projectId;
                if (affectedProject && this.findingUpdatedEventForwardingSubjects[affectedProject]) {
                    this.findingUpdatedEventForwardingSubjects[affectedProject].next(e);
                }
            }

            if (e.eventType === TestMgmtEventTypes.FindingIndexUpdated) {
                const affectedProject = e.projectId;
                if (affectedProject && this.findingIndexUpdatedEventForwardingSubjects[affectedProject]) {
                    this.findingIndexUpdatedEventForwardingSubjects[affectedProject].next(e);
                }
            }

        } else if (e.eventType.startsWith("TestMgmt.TestTemplate.")) {
            // There is a change on one of the templates => Reload templates
            if (this.testTemplatesCache[forProject]) {
                this.api.getTestTemplatesInProject(forProject).pipe(take(1)).subscribe(t => this.testTemplatesCache[forProject].next(t));
            }
        } else if (e.eventType.startsWith("TestMgmt.TestExecution.")) {
            if (e['testTemplateId'] && this.testExecutionRequestsByTemplate[e['testTemplateId']]) {
                this.testExecutionRequestsByTemplate[e['testTemplateId']].fetch();
            }
        } else if (e.eventType.startsWith("TestMgmt.TestObjectCollection.")) {
            const affectedProject = e['projectId'] || forProject;
            const affectedCollectionId = e['collectionId'];

            if (affectedCollectionId && affectedProject) {
                this.testObjectCollectionProjectById[affectedCollectionId] = affectedProject;
            }

            if (affectedProject) {
                this.invalidateTestObjectCollectionsCacheForProject(affectedProject);
            }
        }
    }

    private reloadTestRunById(id: TestRunId) {
        this.api.getTestRun(id).pipe(take(1)).subscribe(r => {
            const subject = this.testRunCache[id];
            if (!!subject) subject.next(r);
        });
    }

    private reloadFindingCacheEntry(id: FindingId): void {
        if (!id) return;
        this.api.getFinding$(id).pipe(take(1)).subscribe(r => {
            if (!this.findingCache[id]) { // should not happen but won't hurt
                this.findingCache[id] = new ReplaySubject<Finding>(1);
            }
            this.findingCache[id].next(r)
        });
    }

    private reloadTestRunsForExecution(execId: TestExecutionId) {
        if (!execId) return;
        this.api.getTestRunsOfTestExecution(execId).pipe(take(1)).subscribe(runs => {
            runs.forEach(r => {
                if (!this.testRunCache[r.testRunId]) this.testRunCache[r.testRunId] = new ReplaySubject<TestRun>(1);
                this.testRunCache[r.testRunId].next(r)
            });
            if (this.testRunsByExecutionCache[execId]) {
                this.testRunsByExecutionCache[execId].next(runs.map(tr => tr.testRunId));
            }
        })
    }

    //  _____  _    _ ____  _      _____ _____            _____ _____
    // |  __ \| |  | |  _ \| |    |_   _/ ____|     /\   |  __ \_   _|
    // | |__) | |  | | |_) | |      | || |         /  \  | |__) || |
    // |  ___/| |  | |  _ <| |      | || |        / /\ \ |  ___/ | |
    // | |    | |__| | |_) | |____ _| || |____   / ____ \| |    _| |_
    // |_|     \____/|____/|______|_____\_____| /_/    \_\_|   |_____|
    //

    getEventsForFindings(projectId: ProjectId, findingIds?: FindingId[]): Observable<any> {
        const findingIdsSet = new Set(findingIds);

        if (findingIds && findingIds.length > 0) {
            return this.getEventsForProject(projectId).pipe(filter(e => {
                switch (e.eventType) {
                    case "TestMgmt.Finding.Updated":
                    case "TestMgmt.Finding.Deleted":
                    case "TestMgmt.Finding.IndexUpdated":
                    case "TestMgmt.Finding.Added": {
                        return e.findings?.some((finding: any) =>
                            findingIdsSet.has(finding.id)
                        ) ?? false;
                    }
                }

                return false;
            }));
        } else {
            return this.getEventsForProject(projectId).pipe(filter(e => e.eventType.startsWith("TestMgmt.Finding")));
        }
    }

    getEventsForProject(projectId: ProjectId): Observable<any> {
        this._registerEventsForProject(projectId);
        return this.eventForwardingSubjects[projectId].asObservable();
    }

    getFindingEventsForAudit(auditId: AuditId): Observable<any> {
        if (!this.auditEventForwardingSubjects[auditId]) {
            this.auditEventForwardingSubjects[auditId] = new Subject<any>();
        }
        return this.auditEventForwardingSubjects[auditId].asObservable();
    }

    getFindingUpdateEventsForProject(projectId: ProjectId): Observable<any> {
        if (!this.findingUpdatedEventForwardingSubjects[projectId]) {
            this.findingUpdatedEventForwardingSubjects[projectId] = new Subject<any>();
        }
        return this.findingUpdatedEventForwardingSubjects[projectId].asObservable();
    }

    getFindingIndexUpdateEventsForProject(projectId: ProjectId): Observable<any> {
        if (!this.findingIndexUpdatedEventForwardingSubjects[projectId]) {
            this.findingIndexUpdatedEventForwardingSubjects[projectId] = new Subject<any>();
        }
        return this.findingIndexUpdatedEventForwardingSubjects[projectId].asObservable();
    }

    startEventPollingForProject(projectId: ProjectId) {
        this._registerEventsForProject(projectId);
    }

    // Test Templates
    copyTestTemplate(projectId: ProjectId, testId: TestTemplateId, name?: string, description?: string, defaults?: AssignmentSpec): Observable<TestTemplateId> {
        return this.api.copyTestTemplate(projectId, testId, name, description, defaults);
    }
    updateTestTemplate(id: TestTemplateId, name?: string, description?: string, defaults?: AssignmentSpec): Observable<GenericApiResponse> {
        return this.api.updateTestTemplate(id, name, description, defaults);
    }
    getTestTemplate(testId: TestTemplateId): Observable<TestTemplate> {
        return this.api.getTestTemplate(testId);
    }
    getTestTemplatesInProject(projectId: ProjectId): Observable<Array<TestTemplate>> {
        return withResultCache<TestTemplate[]>(() => this.api.getTestTemplatesInProject(projectId), this.testTemplatesCache, projectId);
    }
    deleteTestTemplate(testId: TestTemplateId, projectId: ProjectId): Observable<GenericApiResponse> {
        return this.api.deleteTestTemplate(testId).pipe(mergeMap((r) => {
            return this.api.getTestTemplatesInProject(projectId).pipe(take(1), map(t => {
                this.testTemplatesCache[projectId].next(t);
                return r;
            }));
        }));
    }

    // Test Runs
    overrideTestRunEvaluationStatus(testRunId: TestRunId, status: TestRunEvaluationStatus, message?: string): Observable<GenericApiResponse> {
        return this.api.overrideTestRunEvaluationStatus(testRunId, status, message);
    }

    finishTestRun(testRunId: TestRunId, status?: TestRunEvaluationStatus, message?: string): Observable<GenericApiResponse> {
        return this.api.finishTestRun(testRunId, status, message);
    }

    deleteTestRun(testRunId: TestRunId): Observable<GenericApiResponse> {
        return this.api.deleteTestRun(testRunId);
    }

    setTestRunName(testRunId: TestRunId, name: string): Observable<GenericApiResponse> {
        return this.api.setTestRunName(testRunId, name);
    }

    // Test Sessions

    getTestSessionsForProject$(projectId: ProjectId, queryMetadata?: QueryMetadata): ApiSourcedPaginationRequest<TestSessionInfo> {
        return this.api.getTestSessionsInProject$(projectId, queryMetadata);
    }

    createTestSession(projectId: ProjectId, name: string): Observable<TestSessionId> {
        return this.api.createTestSession(projectId, name);
    }

    getTestSession$(testSessionId: TestSessionId): Observable<TestSession> {
        return this.api.getTestSession$(testSessionId);
    }

    // Test Executions

    getTestExecution(id: TestExecutionId): Observable<TestExecutionSummary> {
        return this.api.getTestExecution(id);
    }

    createTestExecution(projectId: ProjectId, testId: TestTemplateId, name: string, s: TestRunCreationSpec, displayType?: string): Observable<CreateTestExecutionResult> {
        return this.api.createAndStartTestExecution(projectId, {id: testId}, s, displayType, name);
    }

    createAndStartTestExecution(
        projectId: ProjectId,
        testId: TestTemplateId,
        name: string,
        s: TestRunCreationSpec,
        displayType?: string,
        automationPolicy?: AutomationPolicyConfig
    ): Observable<CreateTestExecutionResult> {
        return this.api.createAndStartTestExecution(projectId, {id: testId}, s, displayType, name, undefined, automationPolicy);
    }

    createAndStartTestExecutionFromNamedTemplate(projectId: ProjectId, testTemplate: string, name: string, s: TestRunCreationSpec, displayType?: string): Observable<CreateTestExecutionResult> {
        return this.api.createAndStartTestExecution(projectId, {name: testTemplate}, s, displayType, name);
    }

    startTestExecution(executionId: TestExecutionId): Observable<GenericApiResponse> {
        return this.api.startTestExecution(executionId);
    }

    getTestExecutionsForTest(projectId: ProjectId, testId: TestTemplateId): ApiSourcedPaginationRequest<TestExecutionSummary> {
        // TODO return cached values?
        let apiReq = this.api.getTestExecutionsForTest(projectId, testId)
        this.testExecutionRequestsByTemplate[testId] = apiReq;
        return apiReq;
    }

    /**
    * Performs a fresh backend fetch for a test run and retries every <delay> ms for a maximum of <count> times.
    * This is useful to bridge the time between starting a test and being able to retrieve it.
    * Unlike getTestRun(), this is a one-shot fetch and does not stay subscribed to later cache updates.
     * */
    fetchTestRunWithRetry(testRunId: TestRunId, delay: number, count?: number): Observable<TestRun> {
        // The ugly of(undefined) part is necessary to make the retry actually execute the getTestRun function again,
        // instead of just returning its result over and over
        return of(undefined).pipe(mergeMap(() => this.refreshTestRun(testRunId)), retry({delay, count}));
    }

    /**
     * Returns the cached observable for a test run.
     * Subscribers stay connected to later cache updates triggered by refreshes and project events.
     */
    getTestRun(testRunId: TestRunId, forceRefresh: boolean = false): Observable<TestRun> {
        return withResultCache<TestRun>(() => this.api.getTestRun(testRunId), this.testRunCache, testRunId, forceRefresh);
    }

    /**
     * Loads the rebuildable result/timeline projection. This deliberately stays
     * outside the TestRun cache because its revision can advance after the
     * TestRun itself has reached a terminal state.
     */
    getTestRunResultProjection(testRunId: TestRunId): Observable<TestRunResultProjection> {
        return this.api.getTestRunResultProjection(testRunId);
    }

    refreshTestRun(testRunId: TestRunId): Observable<TestRun> {
        if (!this.testRunCache[testRunId]) {
            this.testRunCache[testRunId] = new ReplaySubject<TestRun>(1);
        }
        return this.api.getTestRun(testRunId).pipe(
            take(1),
            tap(testRun => this.testRunCache[testRunId].next(testRun))
        );
    }

    generateTestRunReport(testRunId: TestRunId, config: TestRunReportConfig): Observable<ReportId> {
        return this.api.generateTestRunReport(testRunId, config);
    }

    getTestRunsOfTestExecution(testExecutionId: TestExecutionId): Observable<Array<TestRunId>> {
        if (this.testRunsByExecutionCache[testExecutionId]) {
            return this.testRunsByExecutionCache[testExecutionId].asObservable();
        }
        this.testRunsByExecutionCache[testExecutionId] = new ReplaySubject<Array<TestRunId>>(1);
        return this.api.getTestRunsOfTestExecution(testExecutionId).pipe(mergeMap(runs => {
            runs.forEach(r => {
                if (!this.testRunCache[r.testRunId]) this.testRunCache[r.testRunId] = new ReplaySubject<TestRun>(1);
                this.testRunCache[r.testRunId].next(r)
            });
            this.testRunsByExecutionCache[testExecutionId].next(runs.map(tr => tr.testRunId));
            return this.testRunsByExecutionCache[testExecutionId].asObservable();
        }));
    }

    getFindingsForTestRun(testRunId: TestRunId, forceRefresh: boolean = false): ApiSourcedPaginationRequest<Finding> {
        return this.api.getFindingsForTestRun(testRunId, forceRefresh);
    }

    getFindingInfosForAudit(auditId: AuditId, forceRefresh: boolean = false): Observable<FindingInfo[]> {
        return this.api.getFindingInfosForAudit(auditId, forceRefresh);
    }

    getFinding$(findingId: FindingId, forceRefresh: boolean = false): Observable<Finding> {
        return withResultCache(() => this.api.getFinding$(findingId), this.findingCache, findingId, forceRefresh);
    }

    // does not use the cache, but caches the result
    getFindings$(ids: FindingId[], forceRefresh: boolean = false): Observable<Finding[]> {
        if (ids.length === 0) {
            return of([]);
        }

        // partition the ids into those that are already cached and those that are not
        // then fetch the uncached ones and merge the results

        const uncachedIds = ids.filter(id => !this.findingCache[id]);
        const cachedIds = ids.filter(id => this.findingCache[id]);

        const cachedObservables: Observable<Finding | null>[] = cachedIds.map(id => {
            return this.findingCache[id].asObservable();
        });
        const uncachedObservables: Observable<Finding | null>[] = uncachedIds.map(id => {
            if (!this.findingCache[id]) {
                this.findingCache[id] = new ReplaySubject<Finding>(1);
            }
            return this.api.getFinding$(id).pipe(
                tap(finding => {
                    this.findingCache[id].next(finding);
                }),
                catchError(error => {
                    console.error(`Error fetching finding ${id}:`, error);
                    return of(null);
                })
            );
        });
        const observables: Observable<Finding | null>[] = [...cachedObservables, ...uncachedObservables];

        return combineLatest(observables).pipe(
            map(findings => {
                // sort the findings according to the original ids
                const findingMap = new Map<FindingId, Finding>();
                findings.filter((finding): finding is Finding => !!finding).forEach(finding => {
                    findingMap.set(finding.id, finding);
                });
                return ids.map(id => findingMap.get(id)).filter((finding): finding is Finding => !!finding);
            }),
        );

    }

    // QA Sessions

    getQASessionsInProject$(projectId: ProjectId): Observable<QASession[]> {
        return this.api.getQASessionsInProject$(projectId);
    }

    createQASession$(projectId: ProjectId, data: CreateQASessionData): Observable<QASession> {
        return this.api.createQASession$(projectId, data);
    }

    getQASession$(projectId: ProjectId, sessionId: QASessionId): Observable<QASession> {
        return this.api.getQASession$(projectId, sessionId);
    }

    updateQASession$(projectId: ProjectId, sessionId: QASessionId, data: UpdateQASessionData): Observable<QASession> {
        return this.api.updateQASession$(projectId, sessionId, data);
    }

    closeQASession$(projectId: ProjectId, sessionId: QASessionId): Observable<QASession> {
        return this.api.closeQASession$(projectId, sessionId);
    }

    archiveQASession$(projectId: ProjectId, sessionId: QASessionId): Observable<QASession> {
        return this.api.archiveQASession$(projectId, sessionId);
    }

    getQASessionAssociations$(projectId: ProjectId, sessionId: QASessionId): Observable<QASessionAssociation[]> {
        return this.api.getQASessionAssociations$(projectId, sessionId);
    }

    addQASessionAssociation$(projectId: ProjectId, sessionId: QASessionId, data: CreateQASessionAssociationData): Observable<QASessionAssociation> {
        return this.api.addQASessionAssociation$(projectId, sessionId, data);
    }

    removeQASessionAssociation$(projectId: ProjectId, sessionId: QASessionId, associationId: QASessionAssociationId): Observable<GenericApiResponse> {
        return this.api.removeQASessionAssociation$(projectId, sessionId, associationId);
    }

    getQASessionsByTarget$(projectId: ProjectId, targetType: QASessionAssociationTargetType, targetId: string): Observable<QASession[]> {
        return this.api.getQASessionsByTarget$(projectId, targetType, targetId);
    }


    //// Test Objects
    deleteTestObject$(testObjectId: TestObjectId): Observable<GenericApiResponse> {
        return this.api.deleteTestObject(testObjectId);
    }


    resolveTestObjects$(projectId: ProjectId, sourceSpec: TestObjectsSourceSpec): Observable<TestObjectResolutionJob> {
        return this.api.resolveTestObjects$(projectId, sourceSpec);
    }

    getTestObjectResolutionJob$(projectId: ProjectId, jobId: TestObjectResolutionJobId): Observable<TestObjectResolutionJob> {
        return this.api.getTestObjectResolutionJob$(projectId, jobId);
    }

    captureRepresentativeState$(testObjectId: TestObjectId): Observable<StateCollectorJobId> {
        return this.api.captureRepresentativeState$(testObjectId);
    }

    openTestObjectInExpedition$(testObjectId: TestObjectId, request: OpenTestObjectInExpeditionRequest): Observable<void> {
        return this.api.openTestObjectInExpedition$(testObjectId, request);
    }

    getTestObjects$(ids: TestObjectId[]): Observable<TestObject[]> {
        return this.api.getTestObjects$(ids);
    }

    getTestObjectsInProject$(projectId: ProjectId, queryMetadata?: QueryMetadata): Observable<TestObject[]> {
        return this.api.getTestObjectsInProject$(projectId, queryMetadata);
    }

    getTestObjectStates$(testObjectId: TestObjectId, queryMetadata?: QueryMetadata): Observable<TestObjectStateSummary[]> {
        return this.api.getStatesForTestObject$(testObjectId, queryMetadata).pipe(map(response => response.data ?? []));
    }

    getTestObjectCollectionsInProject$(projectId: ProjectId, includeDeleted: boolean = false): Observable<TestObjectCollectionSummary[]> {
        return this.api.getTestObjectCollectionsInProject$(projectId, includeDeleted).pipe(tap(collections => {
            (collections || []).forEach(collection => {
                this.testObjectCollectionProjectById[collection.collectionId] = collection.projectId;
            });
        }));
    }

    getTestObjectCollection$(collectionId: TestObjectCollectionId, includeDeleted: boolean = false): Observable<TestObjectCollection> {
        return this.api.getTestObjectCollection$(collectionId, includeDeleted).pipe(tap(collection => {
            this.testObjectCollectionProjectById[collection.collectionId] = collection.projectId;
        }));
    }

    createTestObjectCollection$(projectId: ProjectId, dto: CreateTestObjectCollectionDTO): Observable<TestObjectCollection> {
        return this.api.createTestObjectCollection$(projectId, dto).pipe(tap(collection => {
            this.testObjectCollectionProjectById[collection.collectionId] = collection.projectId;
            this.invalidateTestObjectCollectionsCacheForProject(projectId);
        }));
    }

    createTestObject$(projectId: ProjectId, dto: CreateTestObjectDTO): Observable<TestObjectId> {
        return this.api.createTestObject$(projectId, dto);
    }

    updateTestObjectCollection$(collectionId: TestObjectCollectionId, updateActions: TestObjectCollectionUpdateAction[]): Observable<TestObjectCollection> {
        return this.api.updateTestObjectCollection$(collectionId, updateActions).pipe(tap(collection => {
            this.testObjectCollectionProjectById[collection.collectionId] = collection.projectId;
            this.invalidateTestObjectCollectionsCacheForProject(collection.projectId);
        }));
    }

    deleteTestObjectCollection$(collectionId: TestObjectCollectionId): Observable<GenericApiResponse> {
        return this.api.deleteTestObjectCollection$(collectionId).pipe(tap(() => {
            this.invalidateTestObjectCollectionsCacheById(collectionId);
        }));
    }

    getContainingTestObjectCollections$(projectId: ProjectId, testObjectId: TestObjectId): Observable<ContainingTestObjectCollection[]> {
        return this.getTestObjectCollectionDetailsInProject$(projectId).pipe(
            map(collections => buildContainingTestObjectCollections(testObjectId, collections))
        );
    }

    getTestObjectCollectionMembershipCountMap$(projectId: ProjectId): Observable<Record<TestObjectId, number>> {
        return this.getTestObjectCollectionDetailsInProject$(projectId).pipe(
            map(collections => buildCollectionMembershipCountMap(collections))
        );
    }

    updateTestObject(testObjectId: TestObjectId, updateActions?: TestObjectUpdateAction[]): Observable<GenericApiResponse> {
        return this.api.updateTestObject(testObjectId, updateActions || []);
    }

    setTestObjectAutomationPolicy$(testObjectId: TestObjectId, automationPolicy?: AutomationPolicyConfig | null): Observable<GenericApiResponse> {
        return this.updateTestObject(testObjectId, [{
            type: 'SetAutomationPolicy',
            automationPolicy: automationPolicy ?? null,
        }]);
    }

    /**
     * Return if the given test type may be started from the UI.
     * @param testType
     */
    public maybeExecutedInteractively(testType: TestType): boolean {
        return testType === 'expedition_comparison';
    }

    public setTagRule(tagRule: TagRule, projectId: ProjectId): Observable<any> {
        return this.api.setTagRule(tagRule, projectId);
    }

    private addTextToFinding(findingId: FindingId, text: string) {
        const contentCell = {
            type: 'TextContentCell',
            text: text
        } as ContentCell;
        return this.appendCellToFinding(findingId, contentCell);
    }

    addManualFinding(testRunId: TestRunId, issueText: string, severity: IssueSeverity): Observable<FindingId> {
        return this.api.addManualFinding(testRunId, issueText, severity).pipe(mergeMap(id => this.addTextToFinding(id, issueText).pipe(map(() => id))));
    }

    createFinding(projectId: ProjectId, creationData: FindingCreationData): Observable<FindingId> {
        return this.api.createFinding(projectId, creationData);
    }

    createFindingsBulk(projectId: ProjectId, creationDatas: FindingCreationData[]): Observable<FindingId[]> {
        return this.api.createFindingsBulk(projectId, creationDatas);
    }

    createEmptyFinding(projectId: ProjectId, testRun?: TestRunId, audit?: AuditId, affectedSpecs?: SpecId[], observationTargets?: ObservationTarget[]): Observable<FindingId> {
        // return this.api.createFinding();
        return this.api.createFinding(projectId, {
            findingType: 'Generic.Finding',
            severity: 'info',
            issueData: {},
            associatedArtifacts: [],
            observationTargets: observationTargets || [],
            affectedSpecs: affectedSpecs || [],
            testRun: testRun,
            audit: audit,

        } as FindingUpdateData);
    }

    appendCellToFinding(findingId: FindingId, cell: ContentCell): Observable<void> {
        const action: FindingCellAction = {
            type: 'AppendFindingCell',
            cell: cell,
        };
        return this.api.performFindindCellAction(findingId, action);
    }

    appendCellsToFinding(findingId: FindingId, cells: Array<ContentCell>): Observable<void> {
        const action: FindingCellAction = {
            type: 'AppendFindingCells',
            cells: cells,
        };
        return this.api.performFindindCellAction(findingId, action);
    }


    updateFindingCell(findingId: FindingId, cellId: ContentCellId, cell: ContentCell): Observable<void> {
        const action: FindingCellAction = {
            type: 'UpdateFindingCell',
            cellId: cellId,
            cell: cell,
        };
        return this.api.performFindindCellAction(findingId, action);
    }

    deleteFindingCell(findingId: FindingId, cellId: ContentCellId): Observable<void> {
        const action: FindingCellAction = {
            type: 'DeleteFindingCell',
            cellId: cellId,
        };
        return this.api.performFindindCellAction(findingId, action);
    }

    moveFindingCell(findingId: FindingId, cellId: ContentCellId, predecessor?: ContentCellId): Observable<void> {
        const action: FindingCellAction = {
            type: 'MoveFindingCell',
            cellId: cellId,
            predecessor: predecessor,
        };
        return this.api.performFindindCellAction(findingId, action);
    }

    updateFinding(findingId: FindingId, updateData: FindingUpdateData): Observable<GenericApiResponse> {
        return this.api.updateFinding(findingId, updateData);
    }

    updateFindingsBulk(projectId: ProjectId, findingIds: FindingId[], updateData: FindingUpdateData): Observable<{updated: number}> {
        return this.api.updateFindingsBulk(projectId, findingIds, updateData);
    }

    updateFindingsBulkByFilter(projectId: ProjectId, querySpec: FindingsQuerySpec, update: FindingUpdateData): Observable<{updated: number}> {
        return this.api.updateFindingsBulkByFilter(projectId, querySpec, update);
    }

    deleteFinding(findingId: FindingId): Observable<GenericApiResponse> {
        return this.api.updateFinding(findingId, {deleted: true});
    }

    queryFindings$(projectId: ProjectId, querySpec: FindingsQuerySpec): Observable<APIResult<FindingsQueryResult>> {
        return this.api.queryFindings$(projectId,querySpec);
    }

    getFindingsStatsForAudit$(projectId: ProjectId, auditId: AuditId): Observable<Record<string, Record<string, FindingsStats>> | undefined> {
        return this.queryFindings$(projectId, {
            filterSpec: {
                auditFilterSpec: [auditId],
            },
            addSeverityAggregation: true
        }).pipe(
            map(findings => {
                const severityAgg = aggByName(findings.data.aggregations, 'affectedSpecsInfosBySpecSeverity');
                const specAgg = aggByName(severityAgg?.buckets?.[0]?.subAggregations, 'by_spec');
                if (specAgg) {
                    return buildSpecTargetSeverityObject(specAgg);
                } else {
                    return undefined;
                }
            })
        );
    }

    queryFindingsAsCSV$(projectId: ProjectId, querySpec: FindingsQuerySpec): Observable<any> {
        return this.api.queryFindingsAsCSV$(projectId,querySpec);
    }


    // Reports

    public storeTestReportSpec(projectId: ProjectId, name: string, filterSpec: FilterSpec, groupingSpec: GroupingSpec, aggregationSpec: GroupingSpec, shared: boolean): Observable<TestReportSpec> {
        return this.api.storeTestReportSpec(projectId, name, filterSpec, groupingSpec, aggregationSpec, shared);
    }

    public deleteTestReportSpec(projectId: ProjectId, id: ReportSpecId) {
        return this.api.deleteTestReportSpecs(projectId, id);
    }

    public getTestReportSpecs$(projectId: ProjectId): Observable<Array<TestReportSpec>> {
        return this.api.getTestReportSpecs$(projectId);
    }

    public getSpecs$(specsIds: SpecId[]): Observable<Spec[]> {
        return this.api.getSpecs$(specsIds);
    }

    public getSpec$(specId: SpecId): Observable<Spec> {
        return this.api.getSpecs$([specId]).pipe(map(specs => specs[0]));
    }

    public getSpecForestInProject$(projectId: ProjectId, optRoot?: SpecGroupId, maxLevel?: number, includeSpecs?: boolean): Observable<BackendSpecForestNode[]> {
        return this.api.getSpecForestInProject$(projectId, optRoot, maxLevel, includeSpecs);
    }

    public resolveSpecReferences$(specsRefs: SpecRef[]): Observable<Spec[]> {
        return this.api.resolveSpecReferences$(specsRefs);
    }

    public getSpecsInProject$(projectId: ProjectId): Observable<Spec[]> {
        return this.api.getSpecsInProject$(projectId);
    }

    public getSpecCollectionsInProject$(projectId: ProjectId): Observable<SpecCollectionDTO[]> {
        return this.api.getSpecCollectionsInProject$(projectId);
    }

    public getSpecGroups$(ids: SpecGroupId[]): Observable<SpecGroup[]> {
        return this.api.getSpecGroups$(ids);
    }

    public getSpecGroup$(id: SpecGroupId): Observable<SpecGroup> {
        return this.api.getSpecGroups$([id]).pipe(map(groups => groups[0]));
    }

    public updateSpec$(id: SpecId, updateActions: SpecUpdateAction[]): Observable<GenericApiResponse> {
        return this.api.updateSpec(id, updateActions);
    }

    public createSpec(projectId: ProjectId, spec: CreateSpecDTO): Observable<SpecId> {
        return this.api.createSpec(projectId, spec);
    }

    public deleteSpec(id: SpecId): Observable<GenericApiResponse> {
        return this.api.deleteSpec(id);
    }

    public createSpecGroup(projectId: ProjectId, specGroup: CreateSpecGroupDTO): Observable<SpecGroupId> {
        return this.api.createSpecGroup(projectId, specGroup);
    }

    public updateSpecGroup$(id: SpecGroupId, updateActions: SpecGroupUpdateAction[]): Observable<GenericApiResponse> {
        return this.api.updateSpecGroup(id, updateActions);
    }

    public deleteSpecGroup(id: SpecGroupId): Observable<GenericApiResponse> {
        return this.api.deleteSpecGroup(id);
    }

    //// Test Reports

    public queryAdhocTestReports$(projectId: ProjectId, reportSpec: AdHocReportSpec): Observable<any> {
        return this.api.queryAdhocTestReports$(projectId, reportSpec);
    }

    public exportAdhocTestReportAsCsv$(projectId: ProjectId, reportSpec: AdHocReportSpec): Observable<any> {
        return this.api.exportAdhocTestReportAsCsv$(projectId, reportSpec);
    }


    // Issue Reports
    private reloadIssueReporters() {
        if (this.issueReporterCache) {
            this.api.getIssueReportersForCustomer().subscribe(reporters => this.issueReporterCache.next(reporters));
        }
    }

    public getIssueReportersForCustomer(): Observable<Array<IssueReporterIntegration>> {
        if (this.issueReporterCache) {
            return this.issueReporterCache.asObservable();
        }
        return this.api.getIssueReportersForCustomer().pipe(mergeMap(reporter => {
            if (!this.issueReporterCache) {
                this.issueReporterCache = new ReplaySubject<Array<IssueReporterIntegration>>();
            }
            this.issueReporterCache.next(reporter);
            return this.issueReporterCache.asObservable();
        }));
    }

    public getIssueReporterProjectsWithIssueTypes(id: IntegrationId): Observable<IssueReporterProjectWithIssueTypes[]> {
        return this.api.getIssueReporterProjectsWithIssueTypes(id);
    }

    public getIssueReporterParameters(issueReporterId: IntegrationId, issueReporterProjectId: string, issueTypeId: string): Observable<IssueReporterParametersResponse> {
        return this.api.getIssueReporterParams(issueReporterId, issueReporterProjectId, issueTypeId);
    }

    public getIssueReporterDefaults(issueReporterId: IntegrationId, projectId: ProjectId, issueSourceType: IssueSourceType, issueSourceId: string): Observable<IssueReporterDefaults> {
        return this.api.getIssueReporterDefaults(issueReporterId, projectId, issueSourceType, issueSourceId);
    }

    public addIssueReporterForCustomer(config: IssueReporterInsertDTO): Observable<IntegrationId> {
        return this.api.addIssueReporterConfig(config).pipe(tap(() => {
            this.reloadIssueReporters();
        }));
    }

    public submitIssueReport(id: IntegrationId, params: {[s: string]: WMValue<any>}, attachments: ObservationData[], projectId: ProjectId): Observable<IssueReportingInfo> {
        return this.api.submitIssueReport(id, params, attachments, projectId);
    }

    public updateIssueReporterForCustomer(config: IssueReporterIntegration): Observable<GenericApiResponse> {
        return this.api.updateIssueReporterConfig(config).pipe(tap(() => {
            this.reloadIssueReporters();
        }));
    }

    public deleteIssueReporterConfig(issueReporterId: string): Observable<GenericApiResponse> {
        return this.api.deleteIssueReporterConfig(issueReporterId).pipe(tap(() => {
            this.reloadIssueReporters();
        }));
    }

    public createFindingReport(projectId: ProjectId, findings: FindingId[], language?: string): Observable<ReportId> {
        return this.api.createFindingReport(projectId, findings, language);
    }

    private getTestObjectCollectionDetailsInProject$(projectId: ProjectId, forceRefresh: boolean = false): Observable<TestObjectCollection[]> {
        if (!forceRefresh && this.testObjectCollectionDetailsByProjectCache[projectId]) {
            return this.testObjectCollectionDetailsByProjectCache[projectId].asObservable();
        }

        const subject = new ReplaySubject<TestObjectCollection[]>(1);
        this.testObjectCollectionDetailsByProjectCache[projectId] = subject;

        this.getTestObjectCollectionsInProject$(projectId).pipe(
            take(1),
            mergeMap(collections => {
                if (!collections || collections.length === 0) {
                    return of([]);
                }

                return forkJoin(collections.map(collection => this.getTestObjectCollection$(collection.collectionId).pipe(take(1))));
            })
        ).subscribe({
            next: collections => {
                (collections || []).forEach(collection => {
                    this.testObjectCollectionProjectById[collection.collectionId] = collection.projectId;
                });
                subject.next((collections || []).filter(collection => collection.status !== 'deleted'));
            },
            error: error => {
                delete this.testObjectCollectionDetailsByProjectCache[projectId];
                subject.error(error);
            }
        });

        return subject.asObservable();
    }

    private invalidateTestObjectCollectionsCacheById(collectionId: TestObjectCollectionId) {
        const projectId = this.testObjectCollectionProjectById[collectionId];
        if (projectId) {
            this.invalidateTestObjectCollectionsCacheForProject(projectId);
        }
    }

    private invalidateTestObjectCollectionsCacheForProject(projectId: ProjectId) {
        delete this.testObjectCollectionDetailsByProjectCache[projectId];
    }

}

function aggByName(aggs: any[] | undefined, name: string): any | undefined {
  return aggs?.find(a => a.name === name);
}

/**
 * Used for converting the result of a specific aggregation structure (by_spec in findings search logic) into a more usable object.
 */
export function buildSpecTargetSeverityObject(root: any): Record<string, Record<string, FindingsStats>> {
  if (root.name !== 'by_spec') {
    throw new Error(`Expected root aggregation 'by_spec', got '${root.name}'`);
  }

  const out: Record<string, Record<string, FindingsStats>> = {};

  for (const specBucket of root.buckets ?? []) {
    const specId = specBucket.key;
    const findingDocs = aggByName(specBucket.subAggregations, 'finding_docs').buckets[0];
    const byTarget = aggByName(findingDocs.subAggregations, 'by_testobject');
    if (!byTarget) continue;

    const targets: Record<string, FindingsStats> = {};

    for (const targetBucket of byTarget.buckets ?? []) {
      const testObjectId = targetBucket.key;
      const sevAgg = aggByName(targetBucket.subAggregations, 'severity');

      const stats: FindingsStats = { total: targetBucket.count };
      if (sevAgg) {
        for (const sevBucket of sevAgg.buckets ?? []) {
          (stats as Record<string, number | undefined>)[sevBucket.key] = sevBucket.count;
        }
      }

      targets[testObjectId] = stats;
    }

    out[specId] = targets;
  }

  return out;
}
